import React, { FC } from 'react'
import { ArcElement, Chart as ChartJS, DoughnutController, Tooltip } from 'chart.js'
import { Doughnut } from 'react-chartjs-2'
import {
  CurveBody,
  CurveChartCol,
  CurveDonutBox,
  CurveLegend,
  CurvePeriods,
  EmptyState,
  LegendDot,
  LegendItem,
  LegendLabel,
  LegendValue,
  PeriodAcc,
  PeriodMain,
  PeriodMetrics,
  PeriodName,
  PeriodPct,
  PeriodRange,
  PeriodRight,
  PeriodRow,
  PeriodTop,
  PMetric,
  PMetricLabel,
  PMetricVal,
} from './styles'

ChartJS.register(ArcElement, Tooltip, DoughnutController)

export interface SalesCurvePeriodData {
  nome: string
  cor: string
  inicio: string
  fim: string
  dias: number
  pedidos: number
  pedidosPagos: number
  ingressos: number
  ingressosPagos: number
  cortesias: number
  receita: number
  percentual: number
  percentualAcumulado: number
}

export interface SalesCurveData {
  inicio: string | null
  fim: string | null
  totalDias: number
  periodos: SalesCurvePeriodData[]
  totais: { pedidos: number; ingressos: number; receita: number }
}

const hexToRgba = (hex: string, a: number): string => {
  const c = (hex || '#000000').replace('#', '')
  const r = parseInt(c.substring(0, 2), 16)
  const g = parseInt(c.substring(2, 4), 16)
  const b = parseInt(c.substring(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${a})`
}

const brl = (v: number): string =>
  `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`

const nfmt = (v: number): string => Number(v || 0).toLocaleString('pt-BR')

// dd/mm curto a partir de 'YYYY-MM-DD'
const shortDate = (iso: string): string => {
  if (!iso) return ''
  const [, m, d] = iso.split('-')
  return `${d}/${m}`
}

const SalesCurveCard: FC<{ data: SalesCurveData; excludeCortesias?: boolean }> = ({
  data,
  excludeCortesias,
}) => {
  const rawPeriodos = data?.periodos ?? []

  // recomputa ingressos/pedidos/% conforme o toggle (com ou sem cortesias)
  const base = rawPeriodos.map((p) => ({
    ...p,
    ing: excludeCortesias ? p.ingressosPagos : p.ingressos,
    ped: excludeCortesias ? p.pedidosPagos : p.pedidos,
  }))
  const total = base.reduce((s, p) => s + (Number(p.ing) || 0), 0)

  let acc = 0
  const periodos = base.map((p) => {
    const pct = total > 0 ? Number(((p.ing / total) * 100).toFixed(2)) : 0
    acc = Number((acc + pct).toFixed(2))
    return { ...p, pct, acc: Math.min(acc, 100) }
  })

  if (!rawPeriodos.length || total <= 0) {
    return <EmptyState>Sem vendas registradas para montar a curva.</EmptyState>
  }

  const centerText = {
    id: 'curveCenter',
    beforeDatasetsDraw(chart: any) {
      const { ctx } = chart
      const meta = chart.getDatasetMeta(0)
      if (!meta.data.length) return
      const { x, y } = meta.data[0]
      ctx.save()
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillStyle = '#212121'
      ctx.font = '700 24px Inter, sans-serif'
      ctx.fillText(nfmt(total), x, y - 6)
      ctx.fillStyle = '#B0B0B0'
      ctx.font = '11px Inter, sans-serif'
      ctx.fillText('ingressos', x, y + 14)
      ctx.restore()
    },
  }

  const chartData = {
    labels: periodos.map((p) => p.nome),
    datasets: [
      {
        data: periodos.map((p) => p.ing),
        backgroundColor: periodos.map((p) => p.cor),
        borderWidth: 2,
        borderColor: '#fff',
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '64%',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const p = periodos[ctx.dataIndex]
            return ` ${p.nome}: ${p.pct}% (${nfmt(p.ing)})`
          },
        },
      },
    },
  }

  return (
    <CurveBody>
      <CurveChartCol>
        <CurveDonutBox>
          <Doughnut data={chartData} options={options as any} plugins={[centerText]} />
        </CurveDonutBox>
        <CurveLegend>
          {periodos.map((p, i) => (
            <LegendItem key={`${p.nome}-${i}`}>
              <LegendDot style={{ background: p.cor }} />
              <LegendLabel>{p.nome}</LegendLabel>
              <LegendValue>{p.pct}%</LegendValue>
            </LegendItem>
          ))}
        </CurveLegend>
      </CurveChartCol>

      <CurvePeriods>
        {periodos.map((p, i) => (
          <PeriodRow key={`${p.nome}-${i}`} $color={p.cor} $tint={hexToRgba(p.cor, 0.09)}>
            <PeriodMain>
              <PeriodTop>
                <PeriodName>{p.nome}</PeriodName>
                <PeriodRange>
                  {shortDate(p.inicio)} – {shortDate(p.fim)} · {p.dias} dias
                </PeriodRange>
              </PeriodTop>
              <PeriodMetrics>
                <PMetric>
                  <PMetricLabel>Ingressos</PMetricLabel>
                  <PMetricVal>{nfmt(p.ing)}</PMetricVal>
                </PMetric>
                <PMetric>
                  <PMetricLabel>Pedidos</PMetricLabel>
                  <PMetricVal>{nfmt(p.ped)}</PMetricVal>
                </PMetric>
                <PMetric>
                  <PMetricLabel>Receita bruta</PMetricLabel>
                  <PMetricVal>{brl(p.receita)}</PMetricVal>
                </PMetric>
              </PeriodMetrics>
            </PeriodMain>
            <PeriodRight>
              <PeriodPct $color={p.cor}>{p.pct}%</PeriodPct>
              <PeriodAcc>acum. {p.acc}%</PeriodAcc>
            </PeriodRight>
          </PeriodRow>
        ))}
      </CurvePeriods>
    </CurveBody>
  )
}

export default SalesCurveCard
